import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { loadConfig } from "../helpers/config";
import LoadingSpinner from "../components/LoadingSpinner";

function AddBulkWash() {
  const navigate = useNavigate();
  const [apiUrl, setApiUrl] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [tools, setTools] = useState([]);
  const [loadingTools, setLoadingTools] = useState(true);

  const [groupFilter, setGroupFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selectedToolIds, setSelectedToolIds] = useState([]);

  const [washDate, setWashDate] = useState(new Date().toISOString().slice(0, 10));
  const [washedBy, setWashedBy] = useState(localStorage.getItem("username") || "");
  const [washNotes, setWashNotes] = useState("");
  const [washImpregnated, setWashImpregnated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchConfig() {
      const config = await loadConfig();
      setApiUrl(config.APIURL);
      setLoadingConfig(false);
    }
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!loadingConfig) {
      axios.get(`${apiUrl}/tools`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      }).then((response) => {
        setTools(response.data.filter(tool => tool.ToolsGroup?.isClothing && !tool.isDiscarded));
        setLoadingTools(false);
      });
    }
  }, [loadingConfig, apiUrl]);

  const groupNames = useMemo(() => {
    return [...new Set(tools.map(tool => tool.ToolsGroup?.groupname).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [tools]);

  const filteredTools = useMemo(() => {
    const searchTerm = search.toLowerCase();
    return tools.filter(tool => {
      const matchesGroup = groupFilter === "" || tool.ToolsGroup?.groupname === groupFilter;
      const matchesSearch = searchTerm === "" ||
        tool.serialnumber?.toLowerCase().includes(searchTerm) ||
        tool.owner?.toLowerCase().includes(searchTerm);
      return matchesGroup && matchesSearch;
    });
  }, [tools, groupFilter, search]);

  const toggleTool = (toolId) => {
    setSelectedToolIds(prev =>
      prev.includes(toolId) ? prev.filter(id => id !== toolId) : [...prev, toolId]
    );
  };

  const selectAllFiltered = () => {
    const filteredIds = filteredTools.map(tool => tool.id);
    setSelectedToolIds(prev => [...new Set([...prev, ...filteredIds])]);
  };

  const clearSelection = () => setSelectedToolIds([]);

  const handleSubmit = async () => {
    setError("");
    if (selectedToolIds.length === 0) {
      setError("Bitte mindestens ein Gerät auswählen.");
      return;
    }
    if (!washDate) {
      setError("Bitte ein Waschdatum angeben.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${apiUrl}/washlogs/bulk`, {
        toolIds: selectedToolIds,
        washDate,
        washedBy,
        notes: washNotes,
        impregnated: washImpregnated
      }, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      alert(`${selectedToolIds.length} Wascheinträge wurden erfolgreich eingetragen.`);
      navigate("/ToolsGroups");
    } catch (e) {
      setError(e.response?.data?.error || "Fehler beim Speichern der Wascheinträge.");
    }
    setSaving(false);
  };

  if (loadingConfig || loadingTools) {
    return <LoadingSpinner text="Lade Geräte..." />;
  }

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">Sammelwäsche eintragen</div>
        <div className="card-body">
          {error && (
            <div style={{
              background: '#dc3545',
              color: 'white',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '10px',
              fontWeight: 'bold'
            }} role="alert">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="bulkWashDate">Waschdatum:</label>
            <input
              id="bulkWashDate"
              type="date"
              className="form-control"
              value={washDate}
              onChange={e => setWashDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="bulkWashedBy">Gewaschen von:</label>
            <input
              id="bulkWashedBy"
              type="text"
              className="form-control"
              value={washedBy}
              onChange={e => setWashedBy(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="bulkWashNotes">Notiz:</label>
            <input
              id="bulkWashNotes"
              type="text"
              className="form-control"
              value={washNotes}
              onChange={e => setWashNotes(e.target.value)}
            />
          </div>
          <div className="form-group form-check" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label htmlFor="bulkWashImpregnated" className="form-check-label" style={{ marginBottom: 0 }}>Imprägniert</label>
            <input
              id="bulkWashImpregnated"
              type="checkbox"
              className="form-check-input"
              checked={washImpregnated}
              onChange={e => setWashImpregnated(e.target.checked)}
            />
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-header">Geräte auswählen ({selectedToolIds.length} ausgewählt)</div>
        <div className="card-body">
          <div className="form-group" style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <select
              className="form-control"
              style={{ maxWidth: 250 }}
              value={groupFilter}
              onChange={e => setGroupFilter(e.target.value)}
            >
              <option value="">Alle Kleidungsgruppen</option>
              {groupNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: 250 }}
              placeholder="Suche nach Seriennummer/Eigentümer"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button type="button" className="btn btn-secondary btn-sm" onClick={selectAllFiltered}>
              Angezeigte auswählen
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={clearSelection}>
              Auswahl aufheben
            </button>
          </div>

          <table className="table">
            <thead>
              <tr>
                <th></th>
                <th>Gruppe</th>
                <th>Seriennummer</th>
                <th>Eigentümer</th>
              </tr>
            </thead>
            <tbody>
              {filteredTools.length > 0 ? (
                filteredTools.map(tool => (
                  <tr key={tool.id} onClick={() => toggleTool(tool.id)} style={{ cursor: "pointer" }}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedToolIds.includes(tool.id)}
                        onChange={() => toggleTool(tool.id)}
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                    <td>{tool.ToolsGroup?.groupname}</td>
                    <td>{tool.serialnumber}</td>
                    <td>{tool.owner}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">Keine passenden Kleidungsgeräte gefunden</td>
                </tr>
              )}
            </tbody>
          </table>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Speichert..." : `Wäsche für ${selectedToolIds.length} Geräte eintragen`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddBulkWash;
